import React, { Fragment, useState, useEffect } from 'react';
import {
  CButton,
  CCardBody,
  CCollapse,
  CDataTable,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import UserForm from '../containers/UserForm';
import SpinnerWithContainer from '../components/SpinnerWithContainer';
import InviteUserForm from '../containers/InviteUserForm';
import LoaderButton from '../components/LoaderButton';
import { useActions } from '../hooks/useActions';
import {
  getUsers,
  deleteUser,
  resendInvitation,
  exportData,
  RefetchData,
} from '../actions/usersActions';

const fields = [
  { key: 'userId', label: 'User ID' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'reminderText', label: 'Reminder Text' },
  { key: 'feedbackText', label: 'Feedback Text' },
  { key: 'goalSettings', label: 'Goal Setting' },
  {
    key: 'show_details',
    label: 'Options',
    sorter: false,
    filter: false,
  },
];

const Dashboard = () => {
  // get users from store
  const {
    loadingUsers,
    users,
    deletingUser,
    resendingInvitation,
    exportingData,
    refetchingData,
  } = useSelector((state) => state.users);

  // bind dispatch to action creators
  const [
    getUsersAction,
    deleteUserAction,
    resendInvitationAction,
    exportDataAction,
    refetchDataAction,
  ] = useActions([
    getUsers,
    deleteUser,
    resendInvitation,
    exportData,
    RefetchData,
  ]);

  // component state
  const [details, setDetails] = useState([]);
  const [modal, setModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState();
  const [currentlyDeletingUser, setCurrentlyDeletingUser] = useState('');
  const [currentlyResendingUser, setCurrentlyResendingUser] = useState('');
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [currentOperationUser, setCurrentExportingUser] = useState('');

  // useEffect to fetch users
  useEffect(() => {
    getUsersAction();
  }, [getUsersAction]);

  // event handlers
  const toggleDetails = (index) => {
    const position = details.indexOf(index);
    let newDetails = details.slice();
    if (position !== -1) {
      newDetails.splice(position, 1);
    } else {
      newDetails = [...details, index];
    }
    setSelectedUser(users.find((user, indexUser) => indexUser === index));
    setDetails(newDetails);
  };

  const handleUserDelete = () => {
    setCurrentlyDeletingUser(currentlyDeletingUser);
    deleteUserAction(currentlyDeletingUser);
    setDeleteConfirmationModal(false);
  };

  const handleResendInvitaion = (userId) => {
    setCurrentlyResendingUser(userId);
    resendInvitationAction(userId);
  };

  const handleExportData = (userId) => {
    setCurrentExportingUser(userId);
    exportDataAction(userId);
  };

  const handleRefetch = (userId) => {
    setCurrentExportingUser(userId);
    refetchDataAction(userId);
  };

  return (
    <Fragment>
      <h1>Brand Compare</h1>
      {loadingUsers ? (
        <SpinnerWithContainer />
      ) : (
        <Fragment>
          <CModal
            show={deleteConfirmationModal}
            onClose={() => setDeleteConfirmationModal(false)}
          >
            <CModalHeader closeButton>Delete User</CModalHeader>
            <CModalBody>Are you sure you want to delete?</CModalBody>
            <CModalFooter>
              <CButton
                color="danger"
                onClick={() => {
                  handleUserDelete();
                }}
              >
                Delete User
              </CButton>
              <CButton
                color="secondary"
                onClick={() => {
                  setDeleteConfirmationModal(false);
                }}
              >
                Cancel
              </CButton>
            </CModalFooter>
          </CModal>
          <CDataTable
            items={users}
            itemsPerPage={100}
            fields={fields}
            tableFilter
            responsive
            scopedSlots={{
              show_details: (item, index) => {
                return (
                  <td className="py-2">
                    <CButton
                      className="option m-xs-2"
                      color="primary"
                      variant="outline"
                      shape="square"
                      size="sm"
                      onClick={() => {
                        toggleDetails(index);
                      }}
                    >
                      {details.includes(index) ? 'Hide' : 'Show'}
                    </CButton>
                    <LoaderButton
                      title="Delete"
                      loading={
                        deletingUser && item.id === currentlyDeletingUser
                      }
                      disabled={
                        deletingUser && item.id === currentlyDeletingUser
                      }
                      className="option m-xs-2"
                      color="primary"
                      variant="outline"
                      shape="square"
                      size="sm"
                      onClick={() => {
                        setCurrentlyDeletingUser(item.id);
                        setDeleteConfirmationModal(true);
                      }}
                    />
                    <Link to={`/view-data/${item.id}/${item.userId}`}>
                      <CButton
                        className="option m-xs-2"
                        color="primary"
                        variant="outline"
                        shape="square"
                        size="sm"
                      >
                        View Data
                      </CButton>
                    </Link>
                    <LoaderButton
                      title="Resend Invitation"
                      loading={
                        resendingInvitation &&
                        item.id === currentlyResendingUser
                      }
                      disabled={
                        resendingInvitation &&
                        item.id === currentlyResendingUser
                      }
                      className="option"
                      color="primary"
                      variant="outline"
                      shape="square"
                      size="sm"
                      onClick={() => handleResendInvitaion(item.id)}
                    />
                    <LoaderButton
                      loading={
                        exportingData && item.id === currentOperationUser
                      }
                      disabled={
                        exportingData && item.id === currentOperationUser
                      }
                      className="option"
                      color="primary"
                      variant="outline"
                      shape="square"
                      size="sm"
                      onClick={() => handleExportData(item.id)}
                      title="Export Data"
                    />
                    <LoaderButton
                      title="Refetch Data"
                      loading={
                        refetchingData && item.id === currentOperationUser
                      }
                      disabled={
                        refetchingData && item.id === currentOperationUser
                      }
                      className="option"
                      color="primary"
                      variant="outline"
                      shape="square"
                      size="sm"
                      onClick={() => handleRefetch(item.id)}
                    />
                  </td>
                );
              },
              details: (item, index) => {
                return (
                  <CCollapse show={details.includes(index)}>
                    <CCardBody>
                      <UserForm data={selectedUser} />
                    </CCardBody>
                  </CCollapse>
                );
              },
            }}
          />
        </Fragment>
      )}
      <div className="d-flex flex-row">
        <div className="d-flex invite-user-link" onClick={() => setModal(true)}>
          <CIcon name="cil-people" className="people-icon" />
          <h4 className="ml-2">Invite User</h4>
        </div>
        {modal && <InviteUserForm modal={modal} setModal={setModal} />}
      </div>
    </Fragment>
  );
};

export default Dashboard;
